import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Text,
  Flex,
  Badge,
  Tooltip,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  useBreakpointValue,
  ScaleFade,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiClock, FiCalendar } from "react-icons/fi";

// Modern Heat Map component with interactive visualizations
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Convert day index to day name
const getDayName = (index) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days[index];
};

// Get abbreviated day name
const getShortDay = (index) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[index];
};

// Get very short day name (for small screens)
const getTinyDay = (index) => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[index];
};

// Order days correctly for display (order matters for visibility)
const orderedDays = [0, 1, 2, 3, 4, 5, 6]; // Sunday to Saturday

// Format hour for display
const formatHour = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
};

// Get color based on value intensity
const getColorByValue = (value, maxValue) => {
  // No data - transparent
  if (value === 0) return "rgba(245, 245, 245, 0.2)";

  // Theme color with opacity based on value intensity
  const intensity = Math.min(value / (maxValue || 1), 1);

  // Calculate a gradient from light orange to deep orange
  const baseColor = { r: 245, g: 125, b: 54 }; // f57d36 (theme color)

  return `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, ${
    0.2 + intensity * 0.8
  })`;
};

// Get brightness of cell for text contrast
const getBrightness = (value, maxValue) => {
  if (value === 0) return "light";
  const intensity = value / (maxValue || 1);
  return intensity > 0.6 ? "dark" : "light";
};

const HeatMap = ({ data }) => {
  // Theme and styling variables
  const theme = "#f57d36";
  const textColor = useColorModeValue("gray.700", "gray.200");
  const mutedTextColor = useColorModeValue("gray.500", "gray.400");
  const bgColor = useColorModeValue("white", "gray.800");
  const gridBorderColor = useColorModeValue("gray.100", "gray.700");
  const dayLabelBg = useColorModeValue("gray.50", "gray.700");
  const hourLabelBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const summaryBg = useColorModeValue("gray.50", "gray.700");

  // Responsive design - dynamic sizing to fill width
  const containerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const cellSize = useBreakpointValue({ base: 4, sm: 5, md: 6, lg: 8, xl: 10 });
  const dayLabelWidth = useBreakpointValue({ base: 50, sm: 60, md: 70, lg: 80, xl: 90 });
  const useTinyDayNames = useBreakpointValue({ base: true, sm: true, md: false });
  const cellHeight = useBreakpointValue({ base: 20, sm: 22, md: 24, lg: 26, xl: 28 });
  const hourCellWidth =
    containerWidth > 0 ? Math.max((containerWidth - dayLabelWidth) / 24, 4) : 0;

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth(); // Initial measurement
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  const fontSize = useBreakpointValue({ base: "xs", md: "xs" });
  const showHourLabels = useBreakpointValue({ base: false, md: true });

  // State for interactivity
  const [hoveredCell, setHoveredCell] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);

  // Calculate the maximum value for color intensity scaling
  const maxValue = useMemo(() => {
    if (!data || !data.length) return 0;
    return Math.max(...data.flat());
  }, [data]);

  // Handle cell hover - only for tooltip, not for highlighting rows/columns
  const handleCellHover = useCallback((day, hour, value) => {
    setHoveredCell({ value }); // Only store value for tooltip purposes
  }, []);

  // Handle day selection
  const handleDaySelect = useCallback(
    (day) => {
      setSelectedDay(selectedDay === day ? null : day);
      setSelectedHour(null); // Reset hour selection when changing day
    },
    [selectedDay]
  );

  // Handle hour selection
  const handleHourSelect = useCallback(
    (hour) => {
      setSelectedHour(selectedHour === hour ? null : hour);
      setSelectedDay(null); // Reset day selection when changing hour
    },
    [selectedHour]
  );

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedDay(null);
    setSelectedHour(null);
    setHoveredCell(null);
  }, []);

  // Get summary for a day
  const getDaySummary = useCallback(
    (dayIndex) => {
      if (!data || !data[dayIndex])
        return { total: 0, peak: 0, peakHour: null };

      const dayData = data[dayIndex];
      let total = 0;
      let peak = 0;
      let peakHour = null;

      dayData.forEach((value, hour) => {
        total += value;
        if (value > peak) {
          peak = value;
          peakHour = hour;
        }
      });

      return { total, peak, peakHour };
    },
    [data]
  );

  // Get summary for an hour
  const getHourSummary = useCallback(
    (hourIndex) => {
      if (!data) return { total: 0, peak: 0, peakDay: null };

      let total = 0;
      let peak = 0;
      let peakDay = null;

      data.forEach((dayData, day) => {
        const value = dayData[hourIndex] || 0;
        total += value;
        if (value > peak) {
          peak = value;
          peakDay = day;
        }
      });

      return { total, peak, peakDay };
    },
    [data]
  );

  // Get overall heatmap summary
  const getHeatmapSummary = useMemo(() => {
    if (!data)
      return {
        total: 0,
        peakValue: 0,
        peakDay: null,
        peakHour: null,
        avgPerDay: 0,
      };

    let total = 0;
    let peakValue = 0;
    let peakDay = null;
    let peakHour = null;

    data.forEach((dayData, day) => {
      dayData.forEach((value, hour) => {
        total += value;
        if (value > peakValue) {
          peakValue = value;
          peakDay = day;
          peakHour = hour;
        }
      });
    });

    const avgPerDay = total / 7; // 7 days

    return { total, peakValue, peakDay, peakHour, avgPerDay };
  }, [data]);

  // Calculate if a cell should be highlighted - only highlight selected cells, not rows/columns
  const isCellHighlighted = useCallback(
    (day, hour) => {
      if (selectedDay !== null && selectedHour !== null)
        return day === selectedDay && hour === selectedHour;
      if (selectedDay !== null) return day === selectedDay;
      if (selectedHour !== null) return hour === selectedHour;
      return false;
    },
    [selectedDay, selectedHour]
  );

  // Get active summary stats
  const activeSummary = useMemo(() => {
    if (selectedDay !== null) {
      const { total, peak, peakHour } = getDaySummary(selectedDay);
      return {
        type: "day",
        name: getDayName(selectedDay),
        total,
        peak,
        peakTime: peakHour !== null ? formatHour(peakHour) : "N/A",
      };
    }

    if (selectedHour !== null) {
      const { total, peak, peakDay } = getHourSummary(selectedHour);
      return {
        type: "hour",
        name: formatHour(selectedHour),
        total,
        peak,
        peakDay: peakDay !== null ? getDayName(peakDay) : "N/A",
      };
    }

    const { total, peakValue, peakDay, peakHour, avgPerDay } =
      getHeatmapSummary;
    return {
      type: "overall",
      total,
      peak: peakValue,
      avgPerDay: avgPerDay.toFixed(1),
      peakTime:
        peakDay !== null
          ? `${getDayName(peakDay)} at ${formatHour(peakHour)}`
          : "N/A",
    };
  }, [
    selectedDay,
    selectedHour,
    getDaySummary,
    getHourSummary,
    getHeatmapSummary,
  ]);

  return (
    <Box width="100%" ref={containerRef}>
      {/* Header with title and summary information */}
      <Flex direction="column" mb={2}>
        <Flex justify="space-between" align="center" mb={2}>
          <HStack spacing={2}>
            <Icon as={FiCalendar} color={theme} />
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              Usage Patterns
            </Text>
          </HStack>

          <HStack>
            <Badge
              colorScheme="orange"
              variant="subtle"
              fontSize="xs"
              borderRadius="full"
              px={2}
            >
              {getHeatmapSummary.total} Total Sessions
            </Badge>
          </HStack>
        </Flex>

        {/* Summary information based on selection */}
        <ScaleFade in={true} initialScale={0.9}>
          <Box
            p={3}
            bg={summaryBg}
            borderRadius="lg"
            borderLeft="3px solid"
            borderColor={theme}
            mb={2}
            py={2}
          >
            <Text fontSize="sm" fontWeight="medium" color={textColor}>
              {selectedDay !== null && `${getDayName(selectedDay)} Summary:`}
              {selectedHour !== null && `${formatHour(selectedHour)} Summary:`}
              {selectedDay === null &&
                selectedHour === null &&
                "Weekly Overview:"}
            </Text>
            <Flex justify="space-between" mt={2} flexWrap="wrap">
              <HStack spacing={4}>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color={mutedTextColor}>
                    Total Sessions
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color={theme}>
                    {activeSummary.total || "0"}
                  </Text>
                </VStack>

                {activeSummary.type === "day" && (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color={mutedTextColor}>
                      Peak Hour
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {activeSummary.peakTime}
                    </Text>
                  </VStack>
                )}

                {activeSummary.type === "hour" && (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color={mutedTextColor}>
                      Peak Day
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {activeSummary.peakDay}
                    </Text>
                  </VStack>
                )}

                {activeSummary.type === "overall" && (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color={mutedTextColor}>
                      Daily Average
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {activeSummary.avgPerDay}
                    </Text>
                  </VStack>
                )}
              </HStack>

              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color={mutedTextColor}>
                  Peak Time
                </Text>
                <Text fontSize="sm" fontWeight="bold">
                  {activeSummary.type === "overall"
                    ? activeSummary.peakTime
                    : `${activeSummary.peak} sessions`}
                </Text>
              </VStack>
            </Flex>
          </Box>
        </ScaleFade>
      </Flex>

      {/* Interactive heatmap visualization */}
      <Box borderRadius="lg" overflow="hidden" bg={bgColor} boxShadow="sm">
        {/* Hour labels (top) */}
        <Flex align="center">
          {/* Corner cell */}
          <Box
            width={`${dayLabelWidth}px`}
            height={`${cellHeight}px`}
            bg="rgba(255, 255, 255, 0.6)"
            backdropFilter="blur(12px)"
            borderRight="1px solid"
            borderBottom="1px solid"
            borderColor="rgba(245, 125, 54, 0.15)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderTopLeftRadius="md"
          >
            <Icon as={FiClock} color={theme} fontSize="sm" />
          </Box>

          {/* Hour labels */}
          {Array.from({ length: 24 }).map((_, hour) => (
            <MotionBox
              key={`hour-${hour}`}
              width={`${hourCellWidth}px`}
              height={`${cellHeight}px`}
              bg={selectedHour === hour ? "rgba(245, 125, 54, 0.15)" : "rgba(255, 255, 255, 0.6)"}
              backdropFilter="blur(12px)"
              color={selectedHour === hour ? theme : mutedTextColor}
              borderRight="1px solid"
              borderBottom="1px solid"
              borderColor="rgba(245, 125, 54, 0.15)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              fontWeight={selectedHour === hour ? "bold" : "normal"}
              cursor="pointer"
              _hover={{ 
                bg: "rgba(245, 125, 54, 0.08)",
                color: theme
              }}
              onClick={() => handleHourSelect(hour)}
              whileHover={{ y: -1 }}
              transition={{ duration: 0.2 }}
              boxShadow={selectedHour === hour ? "0 0 4px rgba(245, 125, 54, 0.3)" : "none"}
            >
              {showHourLabels
                ? hour % 2 === 0 || selectedHour === hour
                  ? hour
                  : ""
                : ""}
            </MotionBox>
          ))}
        </Flex>

        {/* Day rows with cells */}
        {orderedDays.map((day) => (
          <Flex key={`day-${day}`} align="center">
            {/* Day label */}
            <MotionBox
              key={`day-${day}-label`}
              width={`${dayLabelWidth}px`}
              height={`${cellHeight}px`}
              bg={selectedDay === day ? "rgba(245, 125, 54, 0.15)" : "rgba(255, 255, 255, 0.6)"}
              backdropFilter="blur(12px)"
              color={selectedDay === day ? theme : textColor}
              borderRight="1px solid"
              borderBottom="1px solid"
              borderColor="rgba(245, 125, 54, 0.15)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize={fontSize}
              fontWeight={selectedDay === day ? "bold" : "medium"}
              textAlign="center"
              cursor="pointer"
              _hover={{ 
                bg: `rgba(245, 125, 54, 0.08)`,
                color: selectedDay === day ? "white" : theme
              }}
              onClick={() => handleDaySelect(day)}
              whileHover={{ x: 1 }}
              transition={{ duration: 0.2 }}
              boxShadow={selectedDay === day ? "0 0 4px rgba(245, 125, 54, 0.3)" : "none"}
            >
              {useTinyDayNames ? getTinyDay(day) : getShortDay(day)}
            </MotionBox>

            {/* Hour cells for this day */}
            {Array.from({ length: 24 }).map((_, hour) => {
              const value = data?.[day]?.[hour] || 0;
              const isHighlighted = isCellHighlighted(day, hour);
              const cellBg = getColorByValue(value, maxValue);
              const brightness = getBrightness(value, maxValue);

              return (
                <Tooltip
                  key={`cell-${day}-${hour}`}
                  label={`${getDayName(day)} at ${formatHour(
                    hour
                  )}: ${value} sessions`}
                  placement="top"
                  hasArrow
                >
                  <MotionBox
                    width={`${hourCellWidth}px`}
                    height={`${cellHeight}px`}
                    bg={cellBg}
                    borderRight="1px solid"
                    borderBottom="1px solid"
                    borderColor="rgba(245, 125, 54, 0.15)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="xs"
                    fontWeight="bold"
                    color={brightness === "dark" ? "white" : textColor}
                    cursor="pointer"
                    onMouseEnter={() => handleCellHover(day, hour, value)}
                    onMouseLeave={() => setHoveredCell(null)}
                    _hover={{ transform: "scale(1.05)", zIndex: 2 }}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      scale: isHighlighted ? 1.05 : 1,
                      boxShadow: isHighlighted
                        ? "0 0 4px rgba(0, 0, 0, 0.15)"
                        : "none",
                      zIndex: isHighlighted ? 2 : 1,
                    }}
                    transition={{
                      duration: 0.2,
                      delay: day * 0.05 + hour * 0.002,
                    }}
                  >
                    {value > 0 && value}
                  </MotionBox>
                </Tooltip>
              );
            })}
          </Flex>
        ))}
      </Box>

      {/* Legend */}
      <Flex justify="space-between" mt={2} align="center">
        <Text fontSize="xs" color={mutedTextColor}>
          Showing {getHeatmapSummary.total} sessions across the week
        </Text>

        <HStack spacing={1}>
          <Box w={3} h={3} bg="rgba(245, 125, 54, 0.2)" borderRadius="sm" />
          <Box w={3} h={3} bg="rgba(245, 125, 54, 0.4)" borderRadius="sm" />
          <Box w={3} h={3} bg="rgba(245, 125, 54, 0.6)" borderRadius="sm" />
          <Box w={3} h={3} bg="rgba(245, 125, 54, 0.8)" borderRadius="sm" />
          <Box w={3} h={3} bg="rgba(245, 125, 54, 1)" borderRadius="sm" />
          <Text fontSize="xs" color={mutedTextColor} ml={1}>
            Session Intensity
          </Text>
        </HStack>
      </Flex>
    </Box>
  );
};

export default HeatMap;
